import type { PresentationFrame, SampledEntity, Vec3 } from "../core";

export interface StageProjector {
  projectToStage(position: Vec3): { x: number; y: number; visible: boolean };
}

interface UnitHudVisual {
  root: HTMLDivElement;
  name: HTMLSpanElement;
  healthFill: HTMLSpanElement;
  shieldFill: HTMLSpanElement;
  statuses: HTMLDivElement;
}

export interface HudPoint {
  x: number;
  y: number;
}

/** Keep screen-space HUD cards legible when projected actors overlap. */
export function resolveHudPlacement(candidate: HudPoint, occupied: HudPoint[]): HudPoint {
  const placed = { ...candidate };
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const collision = occupied.some((other) => Math.abs(other.x - placed.x) < 74 && Math.abs(other.y - placed.y) < 34);
    if (!collision) return placed;
    placed.y -= 34;
  }
  return placed;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function hudPosition(entity: SampledEntity): Vec3 {
  const anchor = entity.anchors?.hud ?? { x: 0, y: (entity.scale ?? 1) * 2.85, z: 0 };
  return {
    x: entity.position.x + anchor.x,
    y: entity.position.y + anchor.y,
    z: entity.position.z + anchor.z,
  };
}

/**
 * Full-resolution screen-space HUD. This layer deliberately lives beside the
 * WebGL canvas so pixel scaling, lighting, shaders, and post effects cannot
 * degrade interface text or vital-state bars.
 */
export class HudLayer {
  private readonly root = document.createElement("div");
  private readonly units = new Map<string, UnitHudVisual>();

  constructor(
    mount: HTMLElement,
    private readonly projector: StageProjector,
  ) {
    this.root.className = "hud-layer";
    this.root.setAttribute("aria-label", "Combat HUD");
    mount.append(this.root);
  }

  dispose(): void {
    this.units.clear();
    this.root.remove();
  }

  render(frame: PresentationFrame): void {
    const hudEntities = frame.entities.filter((entity) => entity.visible && entity.kind === "actor" && entity.vitals);
    const activeIds = new Set(hudEntities.map((entity) => entity.id));

    for (const [id, visual] of this.units) {
      if (activeIds.has(id)) continue;
      visual.root.remove();
      this.units.delete(id);
    }

    const projectedEntities = hudEntities
      .map((entity) => ({ entity, projected: this.projector.projectToStage(hudPosition(entity)) }))
      .sort((a, b) => b.projected.y - a.projected.y || a.entity.id.localeCompare(b.entity.id));
    const occupied: HudPoint[] = [];

    for (const { entity, projected } of projectedEntities) {
      const vitals = entity.vitals!;
      const health = clamp01(vitals.health / Math.max(1, vitals.maxHealth));
      const shield = clamp01((vitals.shield ?? 0) / Math.max(1, vitals.maxHealth));
      let visual = this.units.get(entity.id);
      if (!visual) {
        visual = this.createUnit(entity);
        this.units.set(entity.id, visual);
      }

      visual.root.hidden = !projected.visible;
      if (!projected.visible) continue;
      const placement = resolveHudPlacement(projected, occupied);
      occupied.push(placement);
      visual.root.style.left = `${placement.x}px`;
      visual.root.style.top = `${placement.y}px`;
      visual.root.className = `hud-unit hud-unit--${entity.team ?? "neutral"}${health <= 0.25 ? " is-critical" : ""}`;
      visual.root.setAttribute("aria-label", `${entity.label}: ${Math.ceil(vitals.health)} of ${Math.ceil(vitals.maxHealth)} health`);
      visual.name.textContent = entity.label;
      visual.healthFill.style.width = `${health * 100}%`;
      visual.shieldFill.style.width = `${shield * 100}%`;
      visual.shieldFill.hidden = shield <= 0.001;
      this.renderStatuses(visual.statuses, entity.statuses);
    }
  }

  private createUnit(entity: SampledEntity): UnitHudVisual {
    const root = document.createElement("div");
    root.className = `hud-unit hud-unit--${entity.team ?? "neutral"}`;

    const identity = document.createElement("div");
    identity.className = "hud-unit__identity";
    const teamMark = document.createElement("span");
    teamMark.className = "hud-unit__team-mark";
    const name = document.createElement("span");
    name.className = "hud-unit__name";
    identity.append(teamMark, name);

    const bar = document.createElement("div");
    bar.className = "hud-unit__bar";
    const healthFill = document.createElement("span");
    healthFill.className = "hud-unit__health";
    const shieldFill = document.createElement("span");
    shieldFill.className = "hud-unit__shield";
    bar.append(healthFill, shieldFill);

    const statuses = document.createElement("div");
    statuses.className = "hud-unit__statuses";
    root.append(identity, bar, statuses);
    this.root.append(root);
    return { root, name, healthFill, shieldFill, statuses };
  }

  private renderStatuses(mount: HTMLDivElement, statuses: string[]): void {
    const visible = statuses.slice(0, 2);
    const signature = visible.join("|");
    if (mount.dataset.signature === signature) return;
    mount.dataset.signature = signature;
    mount.replaceChildren(...visible.map((status) => {
      const chip = document.createElement("span");
      chip.className = "hud-status";
      chip.textContent = status.slice(0, 3).toUpperCase();
      chip.title = status;
      return chip;
    }));
  }
}
